import { Button } from "./ui/button";
import { Sparkles } from "lucide-react";

/**
 * Presentational "What's New" card — shared by the live popup, the What's New
 * button panel, and the admin live preview so they never drift apart.
 *
 * Props:
 *   announcement: { title, body, image_url, button_label, button_url }
 *   counter:      optional string like "1 / 3"
 *   dismissLabel: label for the dismiss button ("Got it" / "Next" / "← Back")
 *   onCta:        () => void  (button click)  — omit in preview
 *   onDismiss:    () => void                  — omit in preview
 *   preview:      when true, buttons are inert
 */
export default function WhatsNewCard({
  announcement,
  counter,
  dismissLabel = "Got it",
  onCta,
  onDismiss,
  preview = false,
}) {
  const a = announcement || {};
  const hasButton = a.button_label && a.button_url;

  return (
    <div data-testid={preview ? "whats-new-card-preview" : "whats-new-card"} className="flex flex-col max-h-full min-h-0">
      {a.image_url ? (
        <img src={a.image_url} alt={a.title || ""} className="w-full h-32 sm:h-44 object-cover shrink-0" />
      ) : (
        <div className="w-full h-20 bg-gradient-to-r from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
      )}

      <div className="p-6 overflow-y-auto min-h-0 flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-indigo-600">
            <Sparkles className="w-3.5 h-3.5" /> What's new
          </span>
          {counter && <span className="ml-auto text-xs text-gray-400">{counter}</span>}
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{a.title || "Your title appears here"}</h2>
        {a.body && (
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{a.body}</p>
        )}
      </div>

      <div className="flex items-center gap-2 p-4 border-t border-gray-100 bg-white shrink-0">
        {hasButton && (
          <Button
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            onClick={preview ? undefined : onCta}
            disabled={preview}
          >
            {a.button_label}
          </Button>
        )}
        <Button
          variant={hasButton ? "outline" : "default"}
          className="flex-1"
          onClick={preview ? undefined : onDismiss}
          disabled={preview}
        >
          {dismissLabel}
        </Button>
      </div>
    </div>
  );
}
